import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import {
  fetchEventSource,
  EventStreamContentType,
} from "@microsoft/fetch-event-source";
import { message } from "antd";
import { v4 } from "uuid";
import { SESSION_PAGE_SIZE, API_SUCCESS_CODE } from "@/constants";
import {
  SuccessCode,
  ErrorCode,
  DebugOptions,
  FunctionCall,
  SendContent,
  SessionData,
  AgentRunRequest,
  Event,
  FunctionResponse,
  IMessage,
  Part,
} from "@/types";
import {
  fetchAppConfig,
  fetchSessionList,
  fetchSessionDetail,
  closeSession,
  updateSessionTitle,
  addFeedback,
  type ChatConfig,
} from "@/services/api";
import { safeJsonParse } from "@/utils";

const combineTextParts = (parts: Part[]) => {
  const result: Part[] = [];
  let combinedTextPart: Part | undefined;

  // eslint-disable-next-line no-restricted-syntax
  for (const part of parts) {
    // 纯文本 part（没有 fileData 等其他字段）才合并
    const isPureText = part.text && !part.fileData;
    if (isPureText) {
      // 如果没有 combined 或 combined 思考类型跟当前 part 不同，则创建新的合并引用
      if (!combinedTextPart || combinedTextPart.thought !== part.thought) {
        combinedTextPart = { ...part };
        result.push(combinedTextPart);
      } else {
        combinedTextPart.text =
          (combinedTextPart.text || "") + (part.text || "");
      }
    } else {
      combinedTextPart = undefined;
      result.push(part);
    }
  }
  return result;
};

const DEFAULT_SESSION_EVENTS_POLLING_INTERVAL = 10000;

/**
 * 从 event + part 构建非文本 IMessage（functionCall / functionResponse / executableCode 等）。
 */
const buildNonTextMessage = (
  event: Event,
  part: Part,
  role: "user" | "bot" = "bot",
): IMessage => {
  const msg: IMessage = {
    id: v4(),
    author: event.author,
    invocationId: event.invocationId,
    eventId: event.id,
    timestamp: event.timestamp,
    isLike: event.isLike || 0,
    role,
    modelCode: event.modelCode,
    usageMetadata: event.usageMetadata,
    finishReason: event.finishReason,
    raw: event,
  };

  if (part.inlineData) {
    msg.inlineData = {
      displayName: part.inlineData.displayName,
      data: part.inlineData.data,
      mimeType: part.inlineData.mimeType,
    };
  } else if (part.fileData) {
    msg.fileData = [part.fileData];
  } else if (part.functionCall) {
    msg.functionCall = part.functionCall;
  } else if (part.functionResponse) {
    msg.functionResponse = part.functionResponse;
  } else if (part.executableCode) {
    msg.executableCode = part.executableCode;
  } else if (part.codeExecutionResult) {
    msg.codeExecutionResult = part.codeExecutionResult;
  } else if (part.text) {
    msg.text = part.text;
    msg.thought = part.thought;
  }

  return msg;
};

const getMessageEventId = (messageItem: IMessage) =>
  messageItem.eventId || messageItem.raw?.id;

const deduplicateMessages = (messageItems: IMessage[]) => {
  const seenMessageIds = new Set<string>();
  let hasDuplicate = false;

  const uniqueIdMessages = messageItems.filter((messageItem) => {
    if (seenMessageIds.has(messageItem.id)) {
      hasDuplicate = true;
      return false;
    }
    seenMessageIds.add(messageItem.id);
    return true;
  });

  // 机器人文本消息去重：同一 invocationId + 思考类型的文本消息，
  // 如果文本内容存在包含关系，保留最长的（sessionDetails 完整消息），去掉被包含的短文本（SSE 流式增量）。
  // 使用 invocationId 分组，同一轮对话的 invocationId 始终一致， SSE 后续 chunk 的 首个id 与 sessionDetails 的 id 相同，其余不同，
  const botTextGroups = new Map<
    string,
    Array<{ messageItem: IMessage; normalizedText: string }>
  >();
  uniqueIdMessages.forEach((messageItem) => {
    if (
      messageItem.role !== "bot" ||
      !messageItem.invocationId ||
      typeof messageItem.text !== "string"
    )
      return;

    const normalizedText = messageItem.text.replace(/\s+/g, " ").trim();
    if (!normalizedText) return;

    const fileKey = (messageItem.fileData || [])
      .map((file) => file.fileUri)
      .join("|");
    const groupKey = `${messageItem.invocationId}\u0000${Boolean(
      messageItem.thought,
    )}\u0000${fileKey}`;
    const group = botTextGroups.get(groupKey) || [];
    group.push({ messageItem, normalizedText });
    botTextGroups.set(groupKey, group);
  });

  const redundantMessageIds = new Set<string>();
  botTextGroups.forEach((group) => {
    if (group.length <= 1) return;
    const completeTexts: string[] = [];
    [...group]
      .sort((a, b) => b.normalizedText.length - a.normalizedText.length)
      .forEach(({ messageItem, normalizedText }) => {
        if (
          completeTexts.some((completeText) =>
            completeText.includes(normalizedText),
          )
        ) {
          redundantMessageIds.add(messageItem.id);
          hasDuplicate = true;
          return;
        }
        completeTexts.push(normalizedText);
      });
  });

  return hasDuplicate
    ? uniqueIdMessages.filter(
        (messageItem) => !redundantMessageIds.has(messageItem.id),
      )
    : messageItems;
};

const mapSessionMessages = (sessionEvents: any[]): IMessage[] => {
  const mapped: IMessage[] = [];
  sessionEvents.forEach((item) => {
    if (!item || !item.content || !Array.isArray(item.content.parts)) return;
    // 后端为异步任务插入的用户事件仅用于关联任务，不在页面展示。
    if (
      item.author === "user" &&
      item.content.parts.some((part: any) => part.task)
    )
      return;
    // functionResponse 的 user 事件仅用于配对 functionCall，不作为独立消息展示
    if (
      item.content.role === "user" &&
      item.content.parts.every((part: any) => !part || part.functionResponse)
    )
      return;

    const role =
      (item.content.role || "").toLowerCase() === "user" ? "user" : "bot";
    const parts = item.content.parts.filter((part: any) => {
      if (!part) return false;
      return Boolean(
        part.text ||
        part.inlineData ||
        part.functionCall ||
        part.functionResponse ||
        part.fileData ||
        part.executableCode ||
        part.codeExecutionResult ||
        part.errorMessage,
      );
    });
    if (parts.length === 0) return;

    const textParts = parts.filter(
      (part: any) => part.text || part.errorMessage || part.inlineData,
    );
    const fileDataParts = parts.filter((part: any) => part.fileData);
    const otherParts = parts.filter(
      (part: any) =>
        !part.text && !part.errorMessage && !part.inlineData && !part.fileData,
    );

    // 使用确定性 ID：同一服务端事件 + 同一 part 类型始终生成相同 ID，
    // 确保多次 mapSessionMessages 调用产生的消息可以被 id 去重正确识别。
    let partCounter = 0;

    if (textParts.length > 0 || fileDataParts.length > 0) {
      const messageItem: IMessage = {
        id: `session-event:${item.id}:${partCounter++}`,
        author: item.author,
        invocationId: item.invocationId,
        eventId: item.id,
        timestamp: item.timestamp,
        isLike: item.isLike,
        role,
        modelCode: item.modelCode,
        usageMetadata: item.usageMetadata,
        finishReason: item.finishReason,
        raw: item,
      };

      textParts.forEach((part: any) => {
        if (part.inlineData) {
          messageItem.inlineData = {
            displayName: part.inlineData.displayName,
            data: part.inlineData.data,
            mimeType: part.inlineData.mimeType,
          };
        }
        if (part.text) {
          messageItem.text =
            (messageItem.text ? `${messageItem.text}\n` : "") + part.text;
          if (typeof part.thought !== "undefined")
            messageItem.thought = part.thought;
        }
        if (part.errorMessage) {
          messageItem.text =
            (messageItem.text ? `${messageItem.text}\n` : "") +
            part.errorMessage;
        }
      });

      if (fileDataParts.length > 0) {
        messageItem.fileData = fileDataParts.map((part: any) => ({
          displayName: part.fileData.displayName || "",
          fileUri: part.fileData.fileUri,
          mimeType: part.fileData.mimeType,
        }));
      }
      mapped.push(messageItem);
    }

    otherParts.forEach((part: any) => {
      const messageItem: IMessage = {
        id: `session-event:${item.id}:${partCounter++}`,
        author: item.author,
        invocationId: item.invocationId,
        eventId: item.id,
        timestamp: item.timestamp,
        isLike: item.isLike,
        role,
        modelCode: item.modelCode,
        usageMetadata: item.usageMetadata,
        finishReason: item.finishReason,
        raw: item,
      };

      if (part.functionCall) messageItem.functionCall = part.functionCall;
      if (part.executableCode) messageItem.executableCode = part.executableCode;
      if (part.codeExecutionResult)
        messageItem.codeExecutionResult = part.codeExecutionResult;
      if (part.functionResponse) {
        const functionCallMessage = mapped.find(
          (currentMessage) =>
            currentMessage.functionCall?.id === part.functionResponse?.id,
        );
        if (functionCallMessage) {
          functionCallMessage.functionResponse = part.functionResponse;
        }
        return;
      }
      mapped.push(messageItem);
    });
  });
  return mapped;
};

const insertMissingSessionEvents = (
  currentMessages: IMessage[],
  sessionEvents: any[],
  removedInvocationIds?: Set<string>,
) => {
  const renderedEventIds = new Set(
    currentMessages.map(getMessageEventId).filter(Boolean),
  );

  // 从所有 sessionEvents 中提取 functionResponse，配对到 currentMessages 中缺少响应的 functionCall
  // （functionResponse 事件的 role 为 user，不应作为独立消息展示，仅用于补齐工具调用状态）
  let nextMessages = [...currentMessages];
  const fnResMap = new Map<string, any>();
  sessionEvents.forEach((event) => {
    if (event.content?.role !== "user" || !Array.isArray(event.content?.parts))
      return;
    event.content.parts.forEach((p: any) => {
      if (p.functionResponse?.id) {
        fnResMap.set(p.functionResponse.id, p.functionResponse);
      }
    });
  });
  if (fnResMap.size > 0) {
    let paired = false;
    nextMessages = nextMessages.map((msg) => {
      if (msg.functionCall && !msg.functionResponse) {
        const callId = msg.functionCall.id;
        if (callId && fnResMap.has(callId)) {
          paired = true;
          return { ...msg, functionResponse: fnResMap.get(callId) };
        }
      }
      return msg;
    });
    if (!paired) nextMessages = currentMessages;
  }

  const missingEvents = sessionEvents.filter((event) => {
    const parts = event.content?.parts;
    const canRender =
      Boolean(event.id) &&
      event.content?.role === "model" &&
      Array.isArray(parts) &&
      parts.length > 0;
    if (!canRender || renderedEventIds.has(event.id)) return false;

    // reChat 截断后，忽略被删除的 invocationId 对应的旧 bot 消息
    if (
      removedInvocationIds &&
      removedInvocationIds.size > 0 &&
      event.invocationId &&
      removedInvocationIds.has(event.invocationId)
    ) {
      return false;
    }

    return true;
  });
  if (missingEvents.length === 0) return nextMessages;

  const missingMessages = mapSessionMessages(missingEvents);
  if (missingMessages.length === 0) return nextMessages;

  const eventOrder = new Map(
    sessionEvents.map((event, index) => [event.id, index]),
  );
  const getMessageOrder = (messageItem: IMessage) => {
    const eventId = getMessageEventId(messageItem);
    if (eventId && eventOrder.has(eventId)) return eventOrder.get(eventId)!;

    const timestamp = messageItem.timestamp ?? messageItem.raw?.timestamp;
    if (timestamp !== undefined) {
      const nextEventIndex = sessionEvents.findIndex(
        (event) => (event.timestamp || 0) > timestamp,
      );
      return nextEventIndex < 0
        ? sessionEvents.length + 0.5
        : nextEventIndex - 0.5;
    }
    return Number.POSITIVE_INFINITY;
  };

  // nextMessages 已在上方 functionResponse 配对阶段创建，此处直接复用
  missingMessages.forEach((messageItem) => {
    const targetOrder = getMessageOrder(messageItem);
    let insertIndex = nextMessages.findIndex((currentMessage) => {
      const currentOrder = getMessageOrder(currentMessage);
      return Number.isFinite(currentOrder) && currentOrder > targetOrder;
    });
    if (insertIndex < 0) insertIndex = nextMessages.length;
    nextMessages.splice(insertIndex, 0, messageItem);
  });
  return nextMessages;
};

// 后台会话连接状态
interface BackgroundSession {
  ctrl: AbortController;
  messages: IMessage[];
  loading: boolean;
  textMsgRef: IMessage | null;
}

function useADKChat({
  url = window.location.origin,
  token,
  config = {},
  type: _type = "agentDebug",
  enabled = true, // ← 新增: 是否启用 Hook
  strategies,
  onError,
  onMessage,
  onSuccess,
  onStream,
}: DebugOptions) {
  const [loading, setLoadingState] = useState(false);
  const loadingRef = useRef(false);
  const setLoading = useCallback((nextLoading: boolean) => {
    loadingRef.current = nextLoading;
    setLoadingState(nextLoading);
  }, []);
  const [sessionLoading, setSessionLoading] = useState(false);
  const sendingRef = useRef(false); // 同步锁，防止快速重复点击
  const ctrl = useRef<AbortController | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  // 应用配置
  const { appNo, showFirstSession } = config || {};
  const pollSessionEvents = config?.session?.pollSessionEvents ?? true;
  const sessionEventsPollingInterval = Math.max(
    config?.session?.sessionEventsPollingInterval ??
      DEFAULT_SESSION_EVENTS_POLLING_INTERVAL,
    1000,
  );
  const [appInfo, setAppInfo] = useState<ChatConfig | null>(null);
  // 初始化完成
  const [initialized, setInitialized] = useState(false);
  // 会话记录
  const [sessionList, setSessionList] = useState<SessionData[]>([]);
  // 当前会话
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => v4());
  // 会话分页设置
  const [sessionPagination, setSessionPagination] = useState({
    total: 0,
    pageNum: 1,
    pageSize: SESSION_PAGE_SIZE,
  });
  // 开场白
  const [prologue, setPrologue] = useState<string>("");
  // 建议问题
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const pendingSessionEventsRef = useRef<{
    sessionId: string;
    events: any[];
  } | null>(null);
  // 记录上次同步的事件数量，若接口返回长度未变化则跳过后续遍历处理
  const lastSyncedEventsLengthRef = useRef<number>(0);
  const sessionDetailLoadingRef = useRef(false);
  // 用户自定义业务参数 (stateDelta)，每次发送请求时携带
  const stateDeltaRef = useRef<Record<string, any> | undefined>(undefined);
  // reChat 截断消息时记录被删除的 invocationId，防止轮询把旧 bot 回复补回
  const reChatRemovedInvocationIdsRef = useRef<Set<string>>(new Set());

  // ===== 多会话后台连接管理 =====
  const backgroundRef = useRef<Map<string, BackgroundSession>>(new Map());
  const messagesRef = useRef<IMessage[]>(messages);
  messagesRef.current = messages;
  const currentSessionIdRef = useRef<string>(currentSessionId);
  currentSessionIdRef.current = currentSessionId;
  // 合并消息
  const mergedMessages = useMemo(() => {
    const fnResMap: Record<string, FunctionResponse> = {};
    const uniqueMessages = deduplicateMessages(messages);
    uniqueMessages.forEach((msg) => {
      if (msg.functionResponse) {
        fnResMap[msg?.functionResponse?.id || ""] = msg.functionResponse;
      }
    });
    return uniqueMessages.map((msg) => {
      if (msg.functionCall) {
        return {
          ...msg,
          functionResponse: fnResMap[msg?.functionCall?.id || ""],
        };
      }
      return msg;
    });
  }, [messages]);
  const textMsgRef = useRef<IMessage>(null);
  const eventDataRef = useRef<Map<string, any>>(new Map());
  const flushPendingSessionEvents = useCallback(() => {
    const pending = pendingSessionEventsRef.current;
    // 仅在用户正在发送或流式文本拼接时跳过 flush，
    // loading 状态不应阻塞轮询补入（异步任务完成的消息需要在 loading 期间也能补入）
    const replyInProgress = sendingRef.current || textMsgRef.current !== null;
    if (
      !pending ||
      replyInProgress ||
      pending.sessionId !== currentSessionIdRef.current
    )
      return;

    pendingSessionEventsRef.current = null;

    // 使用 functional update 在同一个 setMessages 回调中完成 diff + 写入，
    // 避免双重调用和 loading 闪烁。仅在有实际新消息时才触发 loading 状态。
    setMessages((prev) => {
      const nextMessages = insertMissingSessionEvents(
        prev,
        pending.events,
        reChatRemovedInvocationIdsRef.current,
      );
      if (nextMessages === prev) return prev; // 无变化，不触发 re-render
      messagesRef.current = nextMessages;
      return nextMessages;
    });
  }, []);

  // ===== 多会话轮询事件同步 =====
  const syncSessionEvents = useCallback(
    (events: any[], targetSessionId: string) => {
      if (targetSessionId !== currentSessionIdRef.current) return;
      // 接口返回的事件数量与上次一致，说明列表未发生变化，跳过后续遍历处理
      if (events.length === lastSyncedEventsLengthRef.current) return;
      lastSyncedEventsLengthRef.current = events.length;
      pendingSessionEventsRef.current = {
        sessionId: targetSessionId,
        events,
      };
      flushPendingSessionEvents();
    },
    [flushPendingSessionEvents],
  );

  useEffect(() => {
    pendingSessionEventsRef.current = null;
    lastSyncedEventsLengthRef.current = 0;
  }, [currentSessionId]);

  useEffect(() => {
    if (!loading) flushPendingSessionEvents();
  }, [flushPendingSessionEvents, loading]);

  // 插入消息
  const insertMessage = useCallback((msg: IMessage) => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      if (lastMessage?.isLoading) {
        return [...prev.slice(0, -1), msg, lastMessage];
      }
      return [...prev, msg];
    });
  }, []);

  // 初始化开场白预设置问题
  const insertSuggestedQuestions = useCallback(
    (suggested_questions: string[] = []) => {
      if (!suggested_questions?.length) return;
      // 无消息内容 且 有建议问题时
      setSuggestedQuestions((prev) =>
        messages.length === 0 ? suggested_questions : prev,
      );
    },
    [messages.length],
  );

  // 初始化开场白
  const insertPrologue = useCallback((prologueText: string) => {
    if (prologueText) {
      setPrologue(prologueText);
    }
  }, []);

  // 更新消息
  const updateMessage = useCallback((msg: IMessage) => {
    setMessages((prev) => {
      return prev.map((m) => {
        if (m.id === msg.id) {
          return msg;
        }
        return m;
      });
    });
  }, []);

  const withEventMeta = useCallback((msg: IMessage, event: Event): IMessage => {
    return {
      ...msg,
      modelCode: event.modelCode ?? msg.modelCode,
      usageMetadata: event.usageMetadata ?? msg.usageMetadata,
      finishReason: event.finishReason ?? msg.finishReason,
      raw: {
        ...(msg.raw || {}),
        ...event,
      },
    };
  }, []);

  const updateLastBotEventMeta = useCallback(
    (event: Event) => {
      if (!event.modelCode && !event.usageMetadata && !event.finishReason)
        return;

      setMessages((prev) => {
        const next = [...prev];
        const index = next.findLastIndex((msg) => msg.role === "bot");
        if (index < 0) return prev;

        next[index] = withEventMeta(next[index], event);
        return next;
      });
    },
    [withEventMeta],
  );

  // 存储事件
  const storeEvents = useCallback((part: Part | null, event: any) => {
    let title = "";

    if (part == null && event.actions?.artifactDelta) {
      title += "eventAction: artifact";
    } else if (part) {
      if (part.text) {
        title += `text:${part.text}`;
      } else if (part.functionCall) {
        title += `functionCall:${part.functionCall.name}`;
      } else if (part.functionResponse) {
        title += `functionResponse:${part.functionResponse.name}`;
      } else if (part.executableCode) {
        title += `executableCode:${part.executableCode.code?.slice(0, 10)}`;
      } else if (part.codeExecutionResult) {
        title += `codeExecutionResult:${part.codeExecutionResult.outcome}`;
      } else if (part.errorMessage) {
        title += `errorMessage:${part.errorMessage}`;
      }
    }

    const updatedEvent = { ...event, title };

    const newEventData = new Map(eventDataRef.current);
    newEventData.set(event.id, updatedEvent);
    eventDataRef.current = newEventData;
  }, []);

  // 存储消息
  const storeMessage = useCallback(
    (part: Part | null, event: Event, role: "user" | "bot") => {
      if (!part || Object.keys(part).length === 0) return;

      const msg = buildNonTextMessage(event, part, role);

      // groundingMetadata 仅在前台 SSE 场景需要
      if (
        part.text &&
        event?.groundingMetadata?.searchEntryPoint?.renderedContent
      ) {
        msg.renderedContent =
          event.groundingMetadata.searchEntryPoint.renderedContent;
      }

      insertMessage(msg);
    },
    [insertMessage],
  );

  // 处理消息部分（关键更新）
  const processPart = useCallback(
    (event: Event, part: Part, ignore: boolean = false) => {
      const renderedContent =
        event.groundingMetadata?.searchEntryPoint?.renderedContent;
      if (part.text) {
        if (ignore) return;
        const newChunk = part.text;

        if (
          !textMsgRef.current ||
          textMsgRef.current.thought !== part.thought
        ) {
          const newMessage: IMessage = {
            id: v4(),
            author: event.author,
            invocationId: event.invocationId,
            eventId: event.id,
            timestamp: event.timestamp,
            role: "bot",
            text: newChunk,
            isLike: event.isLike || 0,
            thought: part.thought,
            modelCode: event.modelCode,
            usageMetadata: event.usageMetadata,
            finishReason: event.finishReason,
            raw: event,
          };

          if (renderedContent) {
            newMessage.renderedContent = renderedContent;
          }

          textMsgRef.current = newMessage;
          // 存储原数据
          storeEvents(part, event);
          // 插入消息
          insertMessage(newMessage);
        } else {
          if (renderedContent) {
            textMsgRef.current = {
              ...textMsgRef.current,
              renderedContent,
            };
          }
          textMsgRef.current = {
            ...withEventMeta(textMsgRef.current, event),
            text: (textMsgRef.current.text || "") + newChunk,
          };
          updateMessage(textMsgRef.current);
          if (event.partial === false) {
            storeEvents(part, event);
            textMsgRef.current = null;
          }
        }
      } else {
        textMsgRef.current = null;
        storeEvents(part, event);
        storeMessage(part, event, "bot");
      }
    },
    [storeEvents, storeMessage, insertMessage, updateMessage, withEventMeta],
  );

  // 处理建议类消息
  const processFollowupPart = useCallback(
    (event: Event) => {
      // 获取建议类消息的文本内容
      const part = event?.content?.parts?.[0] || null;
      const text = part?.text || "";
      storeEvents(part, event);
      if (text) {
        setSuggestedQuestions((prev) => [...prev, text]);
      }
    },
    [storeEvents],
  );

  // 处理错误消息
  const processErrorMessage = useCallback(
    (chunkJson: any) => {
      storeEvents(null, chunkJson);
      insertMessage({
        id: v4(),
        text: chunkJson.errorMessage,
        role: "bot",
      });
    },
    [storeEvents, insertMessage],
  );

  // 处理动作和制品
  const processActionArtifact = useCallback(
    (event: any) => {
      if (event.actions && event.actions.artifactDelta) {
        storeEvents(null, event);
        storeMessage(null, event, "bot");
      }
    },
    [storeEvents, storeMessage],
  );

  // 发送消息请求
  const sendMessageRequest = useCallback(
    (values: AgentRunRequest) => {
      return new Promise<void>((resolve) => {
        const targetSessionId = values.sessionId || currentSessionIdRef.current;

        // 只 abort 同一 session 的旧连接
        if (targetSessionId === currentSessionIdRef.current && ctrl.current) {
          ctrl.current.abort();
        }
        const newCtrl = new AbortController();
        if (targetSessionId === currentSessionIdRef.current) {
          setLoading(true);
          ctrl.current = newCtrl;
        }

        // 当 signal 被外部 abort（stopChat）时，确保外层 Promise 被 resolve
        // 否则 startChat 中的 await 会永远挂起，sendingRef 无法重置为 false
        newCtrl.signal.addEventListener(
          "abort",
          () => {
            resolve();
          },
          { once: true },
        );

        const requestUrl = `${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sendMsgStreaming`;

        // 后台流式文本拼接
        let bgTextMsg: IMessage | null = null;

        fetchEventSource(requestUrl, {
          method: "POST",
          signal: newCtrl.signal,
          body: JSON.stringify(values),
          openWhenHidden: true,
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            stream: "true",
            Authorization: `Bearer ${tokenRef.current}`,
          },
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get("content-type") === EventStreamContentType
            ) {
              // everything's good
            } else if (response.status === 401 || response.status === 403) {
              setLoading(false);
              onError?.({
                code: ErrorCode.AUTH_ERROR,
                message:
                  response.status === 401
                    ? "认证失败，token 无效或已过期"
                    : "无权限访问该资源",
              });
              resolve();
            } else if (
              response.status >= 400 &&
              response.status < 500 &&
              response.status !== 429
            ) {
              resolve();
            } else {
              resolve();
            }
          },
          onmessage: (event) => {
            const rawData = safeJsonParse(event.data, {});

            // 处理新的数据格式: { data: {...}, result: {...} }
            let chunkJson: Event = rawData;
            if (rawData.data && rawData.result) {
              // 转换为旧格式
              chunkJson = {
                ...rawData.data,
                content: rawData.data,
                invocationId: rawData.result.invocationId,
                sessionId: rawData.result.sessionId,
                id:
                  rawData.data.id ||
                  rawData.result.id ||
                  rawData.result.eventId ||
                  rawData.result.invocationId,
              };
            }

            if (chunkJson.error) {
              message.warning(chunkJson.error);
              return;
            }

            // 判断是否为后台 session
            const isBackground =
              currentSessionIdRef.current !== targetSessionId;

            if (isBackground) {
              // 后台 session：更新 backgroundRef 中的消息
              const bg = backgroundRef.current.get(targetSessionId);
              if (!bg) return;

              if (chunkJson.content) {
                if (chunkJson.content.role === "followup") return;
                for (const part of combineTextParts(chunkJson.content.parts)) {
                  const isPureText = part.text && !part.fileData;
                  if (isPureText) {
                    if (chunkJson?.partial === false) continue;
                    const newChunk = part.text || "";
                    if (!bgTextMsg || bgTextMsg?.thought !== part.thought) {
                      const newMessage: IMessage = {
                        id: v4(),
                        author: chunkJson.author,
                        invocationId: chunkJson.invocationId,
                        eventId: chunkJson.id,
                        timestamp: chunkJson.timestamp,
                        role: "bot",
                        text: newChunk,
                        thought: part.thought,
                      };
                      bgTextMsg = newMessage;
                      bg.messages = [...bg.messages, newMessage];
                    } else {
                      bgTextMsg = {
                        ...bgTextMsg,
                        text: (bgTextMsg.text || "") + newChunk,
                      };
                      bg.messages = bg.messages.map((m) =>
                        m.id === bgTextMsg!.id ? bgTextMsg! : m,
                      );
                      if (chunkJson.finishReason === "stop") {
                        bgTextMsg = null;
                      }
                    }
                  } else {
                    bgTextMsg = null;
                    bg.messages = [
                      ...bg.messages,
                      buildNonTextMessage(chunkJson, part),
                    ];
                  }
                }
              } else if (chunkJson.errorMessage) {
                bg.messages = [
                  ...bg.messages,
                  { id: v4(), text: chunkJson.errorMessage, role: "bot" },
                ];
              }
              bg.textMsgRef = bgTextMsg;
              return;
            }

            // 前台 session：正常处理
            onStream?.(chunkJson);
            updateLastBotEventMeta(chunkJson);
            if (chunkJson.content) {
              // 建议类消息单独处理
              if (chunkJson.content.role === "followup") {
                processFollowupPart(chunkJson);
                return;
              }
              // eslint-disable-next-line no-restricted-syntax
              for (const part of combineTextParts(chunkJson.content.parts)) {
                // partial为 false 时忽略该条消息文本
                processPart(chunkJson, part, chunkJson?.partial === false);
              }
            } else if (chunkJson.errorMessage) {
              processErrorMessage(chunkJson);
            } else if (chunkJson.actions) {
              processActionArtifact(chunkJson);
            }
          },
          onclose: () => {
            const isBackground =
              currentSessionIdRef.current !== targetSessionId;
            if (isBackground) {
              const bg = backgroundRef.current.get(targetSessionId);
              if (bg) {
                bg.loading = false;
                bg.textMsgRef = null;
                // 标记 SSE 已完成，使恢复时 signal.aborted 能正确判断
                bg.ctrl.abort();
              }
              // 如果该后台会话已被恢复到前台（backgroundRef 中已删除），
              // 需要更新前台 loading 状态
              if (
                !backgroundRef.current.has(targetSessionId) &&
                currentSessionIdRef.current === targetSessionId
              ) {
                setLoading(false);
                textMsgRef.current = null;
              }
            } else {
              setLoading(false);
              if (textMsgRef.current) {
                onMessage?.(textMsgRef.current?.text || "", textMsgRef.current);
              }
              textMsgRef.current = null;
              ctrl.current = null;
              newCtrl.abort();
            }
            resolve();
          },
          onerror: (error) => {
            const isBackground =
              currentSessionIdRef.current !== targetSessionId;
            if (isBackground) {
              const bg = backgroundRef.current.get(targetSessionId);
              if (bg) {
                bg.loading = false;
                bg.ctrl.abort();
              }
              // 同上：后台会话已恢复到前台时同步 loading 状态
              if (
                !backgroundRef.current.has(targetSessionId) &&
                currentSessionIdRef.current === targetSessionId
              ) {
                setLoading(false);
                ctrl.current = null;
              }
            } else {
              setLoading(false);
              ctrl.current = null;
              newCtrl.abort();
            }
            resolve();
            console.error("EventSource failed:", error);
            throw error;
          },
        });
      });
    },
    [
      processActionArtifact,
      processErrorMessage,
      processPart,
      updateLastBotEventMeta,
    ],
  );

  // 发送会话
  const startChat = async ({
    text = "",
    files = [],
    functionResponse,
  }: SendContent) => {
    if (loadingRef.current || sendingRef.current) return;
    if (!text.trim() && !files?.length && !functionResponse) return;
    sendingRef.current = true;
    // 清空建议问题
    setSuggestedQuestions([]);
    // 正常发送时重置 reChat 删除标记
    reChatRemovedInvocationIdsRef.current = new Set();
    // 发送消息
    setMessages((prev) => {
      let temp = [...prev];
      // 如果 text 存在
      if (text.trim()) {
        temp.push({
          id: v4(),
          role: "user",
          text,
        });
      }
      // 如果file 存在
      if (files.length > 0) {
        temp.push({
          id: v4(),
          role: "user",
          fileData: files.map((file) => ({
            displayName: file.fileName || file.name,
            mimeType: file.mimeType || file.response?.mimeType,
            fileUri:
              file.tempUrl || file.response?.fileUrl || file.response?.tempUrl,
          })),
        });
      }
      // 如果 functionResponse 存在
      if (functionResponse) {
        temp.push({
          id: v4(),
          role: "user",
          functionResponse,
        });
      }
      return temp;
    });
    try {
      await sendMessageRequest({
        stream: true,
        sessionId: currentSessionId,
        content: {
          role: "user",
          parts: functionResponse ? [{ functionResponse }] : [{ text }],
        },
        files: files.map((file) => ({
          fileName: file.fileName || file.response?.fileName,
          fileId: file.fileId || file.response?.fileId,
          tempUrl:
            file.tempUrl || file.response?.fileUrl || file.response?.tempUrl,
          type: file.type || file.response?.fileType,
          mimeType: file.mimeType || file.response?.mimeType,
        })),
        ...(stateDeltaRef.current !== undefined && {
          stateDelta: stateDeltaRef.current,
        }),
      });
    } finally {
      sendingRef.current = false;
    }
    // 获取聊天记录
    initAppConversations();
  };

  // 重新发送会话
  const reChat = async () => {
    if (loadingRef.current || sendingRef.current) return;
    if (messages.length === 0) return;
    sendingRef.current = true;
    // 清空建议问题
    setSuggestedQuestions([]);

    // 找到最后一条用户消息的索引，删除其后所有 bot 消息
    const lastUserIndex = messages.findLastIndex(
      (msg) => msg.role === "user" && !msg.functionResponse,
    );
    if (lastUserIndex === -1) {
      sendingRef.current = false;
      return;
    }

    const newMessages = messages.slice(0, lastUserIndex + 1);
    setMessages(newMessages);

    // 收集被截断的 bot 消息的 invocationId，轮询时忽略这些旧回复
    const removedInvocationIds = new Set<string>();
    for (let i = lastUserIndex + 1; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.invocationId) removedInvocationIds.add(msg.invocationId);
    }
    reChatRemovedInvocationIdsRef.current = removedInvocationIds;

    const lastUserMessage = newMessages[lastUserIndex];
    const { text = "", fileData = [] } = lastUserMessage;

    try {
      await sendMessageRequest({
        stream: true,
        sessionId: currentSessionId,
        content: {
          role: "user",
          parts: [{ text }],
        },
        files: fileData?.map((file) => ({
          fileName: file.displayName,
          tempUrl: file.fileUri,
          mimeType: file.mimeType,
        })),
        ...(stateDeltaRef.current !== undefined && {
          stateDelta: stateDeltaRef.current,
        }),
      });
    } finally {
      sendingRef.current = false;
    }
  };

  // 确认函数调用
  const confirmFnCall = (fnCall: FunctionCall, confirmed: boolean) => {
    const functionResponse = strategies?.createFunctionResponse?.(
      fnCall,
      confirmed,
    ) ?? {
      id: fnCall.id,
      name: fnCall.name,
      response: { confirmed },
    };
    startChat({ functionResponse });
  };

  // 使用建议问题
  const suggestChat = (text: string) => {
    if (loadingRef.current) return;
    if (!text.trim()) return;
    setSuggestedQuestions([]);
    startChat({ text });
  };

  // 停止会话（只中断前台连接）
  const stopChat = useCallback(() => {
    pendingSessionEventsRef.current = null;
    ctrl.current?.abort();
    ctrl.current = null;
    setLoading(false);
    textMsgRef.current = null;
  }, [setLoading]);

  // 清除/重置会话
  const clearChat = () => {
    const newSessionId = v4();
    setCurrentSessionId(newSessionId);
    // 清除 reChat 删除标记
    reChatRemovedInvocationIdsRef.current = new Set();
    // 重置聊天内容
    stopChat();
    setPrologue(appInfo?.onboardingInfo?.prologue || "");
    setMessages([]);
    setSuggestedQuestions(appInfo?.onboardingInfo?.suggested_questions || []);
  };

  // 格式化会话记录
  const formatMessages = useCallback(
    (sessionEvents: any[], isReplace = false) => {
      const mapped = mapSessionMessages(sessionEvents);
      setMessages((prev) => (isReplace ? mapped : [...prev, ...mapped]));
    },
    [],
  );

  // 切换设置会话详情
  const setCurrentSession = async (sessionId: string) => {
    if (sessionId) {
      if (sessionId === currentSessionId) {
        return;
      }

      pendingSessionEventsRef.current = null;
      // 切换会话时重置 reChat 删除标记
      reChatRemovedInvocationIdsRef.current = new Set();

      // 1. 将当前会话挂起到后台（不中断 SSE 连接）
      const prevSessionId = currentSessionId;
      if (ctrl.current) {
        // 有活跃流式连接，保存到后台
        backgroundRef.current.set(prevSessionId, {
          ctrl: ctrl.current,
          messages: [...messagesRef.current],
          loading: true,
          textMsgRef: textMsgRef.current,
        });
        ctrl.current = null;
        textMsgRef.current = null;
        setLoading(false);
      } else if (messagesRef.current.length > 0) {
        // 无活跃连接但有消息，缓存用于切回
        const dummyCtrl = new AbortController();
        dummyCtrl.abort(); // 标记为已完成
        backgroundRef.current.set(prevSessionId, {
          ctrl: dummyCtrl,
          messages: [...messagesRef.current],
          loading: false,
          textMsgRef: null,
        });
      }

      // 2. 尝试从后台恢复目标会话
      const bg = backgroundRef.current.get(sessionId);
      if (bg) {
        setMessages(bg.messages);
        messagesRef.current = bg.messages;
        textMsgRef.current = bg.textMsgRef;
        // 仅当 SSE 连接还在运行时才恢复 loading 状态，
        // 避免 SSE 已关闭但 bg 快照中 loading 仍为 true 导致 sender 永久卡住
        const isSseStillActive = bg.loading && !bg.ctrl.signal.aborted;
        setLoading(isSseStillActive);
        ctrl.current = isSseStillActive ? bg.ctrl : null;
        setCurrentSessionId(sessionId);
        setInitialized(true);
        backgroundRef.current.delete(sessionId);
        return;
      }

      // 3. 后台没有目标 session，走 API 加载（原有逻辑）
      setInitialized(false);
      setCurrentSessionId(sessionId);
      sessionDetailLoadingRef.current = true;
      try {
        const { data, result } = await fetchSessionDetail({
          url,
          appNo,
          sessionId,
          token: tokenRef.current,
        });

        if (result?.code === API_SUCCESS_CODE) {
          if (Array.isArray(data) && data.length > 0) {
            // 有内容：清空开场白与建议问题，仅展示历史消息
            setPrologue("");
            formatMessages(data, true);
            setSuggestedQuestions([]);
          } else {
            // 空会话：恢复开场白与预制建议问题
            setPrologue(appInfo?.onboardingInfo?.prologue || "");
            setMessages([]);
            messagesRef.current = [];
            textMsgRef.current = null;
            setSuggestedQuestions(
              appInfo?.onboardingInfo?.suggested_questions || [],
            );
          }
        }
      } finally {
        sessionDetailLoadingRef.current = false;
        setInitialized(true);
      }
    } else {
      setCurrentSessionId(v4());
    }
  };

  // 获取聊天记录
  const initAppConversations = async (fetchDetail = false) => {
    try {
      setSessionLoading(true);

      const {
        data = [],
        pagination,
        result,
      } = await fetchSessionList({
        url,
        appNo,
        token: tokenRef.current,
        pageSize: sessionPagination.pageSize,
      });

      if (result?.code === API_SUCCESS_CODE) {
        if (data.length > 0) {
          const formatSessions = data.map((item) => {
            return {
              key: item.sessionId,
              label: item.title || "新对话",
              timestamp: new Date(item.startTime).getTime(),
              messages: [],
              meta: {
                ...item,
              },
            };
          });

          setSessionList(formatSessions);
          // 设置分页信息
          setSessionPagination({
            pageNum: sessionPagination.pageNum,
            pageSize: sessionPagination.pageSize,
            total: pagination?.total || 0,
          });
          const { sessionId } = data?.[0];
          if (showFirstSession) {
            setCurrentSession(sessionId);
          } else {
            fetchDetail && setPrologue(appInfo?.onboardingInfo?.prologue || "");
            fetchDetail &&
              setSuggestedQuestions(
                appInfo?.onboardingInfo?.suggested_questions || [],
              );
          }
          // 成功回调
          if (!initialized) {
            onSuccess?.({
              code: SuccessCode.APP_MESSAGES_INIT_SUCCESS,
              message: "初始化聊天记录成功",
              data: {
                sessionId,
              },
            });
            setInitialized(true);
          }
        } else {
          // 无会话记录时，初始化会话详情
          clearChat();
        }
      } else {
        // 服务端业务错误，透传原始 code + message 给接入方自行处理
        onError?.({
          code: result?.code || "UNKNOWN_ERROR",
          message: result?.message || "获取会话列表失败",
          raw: result,
        });
        setSessionList([]);
        setCurrentSessionId(v4());
        setPrologue("");
        setSuggestedQuestions([]);
      }
    } catch {
      onError?.({
        code: ErrorCode.NETWORK_ERROR,
        message: "获取会话列表失败",
      });
    } finally {
      setSessionLoading(false);
    }
  };

  // 删除会话
  const deleteSession = async (sessionId: string) => {
    if (!sessionId) return;
    const { result } = await closeSession({
      url,
      appNo,
      sessionId,
      token: tokenRef.current,
    });
    if (result?.code === API_SUCCESS_CODE) {
      // 删除成功后，重新获取会话列表
      initAppConversations(true);
      // 如果删除的是当前会话，则创建新的会话
      if (sessionId === currentSessionId) {
        clearChat();
      }
    } else {
      message.error("删除会话失败");
    }
  };

  // 点赞/点踩
  const likeMessage = async (
    invocationId: string,
    isLike: 1 | -1,
    feedbackData?: { feedbackTags?: string; feedbackDescription?: string },
  ) => {
    if (!appNo || !invocationId) return false;
    const { result } = await addFeedback({
      url,
      appNo,
      token: tokenRef.current,
      requestId: invocationId,
      isLike,
      ...feedbackData,
    });
    if (result?.code === API_SUCCESS_CODE) {
      setMessages((prev) =>
        prev.map((m) =>
          m.invocationId === invocationId ? { ...m, isLike } : m,
        ),
      );
      return true;
    }
    return false;
  };

  // 更新会话名称
  const updateSession = async (sessionId: string, title: string) => {
    if (!sessionId || !title) return;
    const { result } = await updateSessionTitle({
      url,
      appNo,
      title,
      sessionId,
      token: tokenRef.current,
    });
    if (result?.code === API_SUCCESS_CODE) {
      // 编辑成功后，重新获取会话列表
      initAppConversations();
    } else {
      message.error("更新会话失败");
    }
  };

  // 初始化应用
  const initConfig = async () => {
    try {
      setSessionLoading(true);
      const { data, result } = await fetchAppConfig({ url, appNo });
      if (result.code === API_SUCCESS_CODE) {
        setAppInfo(data);
        // 成功回调
        onSuccess?.({
          code: SuccessCode.APP_CONFIG_INIT_SUCCESS,
          message: "初始化配置成功",
        });
      } else {
        // 服务端业务错误，透传原始 code + message
        onError?.({
          code: result.code || "UNKNOWN_ERROR",
          message: result.message || "获取应用配置失败",
          raw: result,
        });
      }
    } catch (error) {
      onError?.({
        code: ErrorCode.NETWORK_ERROR,
        message: "获取应用配置失败",
      });
    } finally {
      setSessionLoading(false);
    }
  };

  // 初始化—获取配置 (仅在启用时)
  useEffect(() => {
    if (enabled && url && appNo) {
      initConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, url, appNo]);

  // 初始化-聊天记录
  useEffect(() => {
    if (appInfo) {
      initAppConversations(true);
    }
  }, [appInfo]);

  // 轮询当前会话详情，空闲时补入后端异步任务产生的模型消息。
  useEffect(() => {
    if (
      !enabled ||
      !initialized ||
      !pollSessionEvents ||
      !appNo ||
      !currentSessionId
    )
      return;

    let disposed = false;
    let requesting = false;
    const pollingSessionId = currentSessionId;

    const poll = async () => {
      if (disposed || requesting || sessionDetailLoadingRef.current) return;
      requesting = true;
      try {
        const { data, result } = await fetchSessionDetail({
          url,
          appNo,
          sessionId: pollingSessionId,
          token: tokenRef.current,
        });
        if (
          !disposed &&
          result?.code === API_SUCCESS_CODE &&
          Array.isArray(data)
        ) {
          syncSessionEvents(data, pollingSessionId);
        }
      } catch {
        // 新会话尚未落库或短暂网络异常时等待下一轮，不打断当前聊天。
      } finally {
        requesting = false;
      }
    };

    // 不立即执行首次 poll，避免与 initAppConversations / setCurrentSession / fetchSessionDetail 竞争。首次数据由初始化流程加载，轮询仅负责后续增量同步。
    const timer = window.setInterval(poll, sessionEventsPollingInterval);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [
    appNo,
    currentSessionId,
    enabled,
    initialized,
    pollSessionEvents,
    sessionEventsPollingInterval,
    syncSessionEvents,
    url,
  ]);

  // 组件卸载时清理所有连接（前台 + 后台）
  useEffect(() => {
    return () => {
      ctrl.current?.abort();
      backgroundRef.current.forEach((bg) => bg.ctrl?.abort());
      backgroundRef.current.clear();
    };
  }, []);

  return {
    appInfo,
    startChat,
    reChat,
    stopChat,
    clearChat,
    suggestChat,
    confirmFnCall,
    initialized,
    currentSessionId,
    sessionList,
    sessionPagination,
    prologue,
    suggestions: suggestedQuestions,
    loading,
    sessionLoading,
    messages: mergedMessages,
    insertSuggestedQuestions,
    insertPrologue,
    deleteSession,
    updateSession,
    setSuggestedQuestions,
    setCurrentSession,
    setMessages,
    storeEvents,
    storeMessage,
    likeMessage,
    setStateDelta: (delta: Record<string, any> | undefined) => {
      stateDeltaRef.current = delta;
    },
  };
}

export default useADKChat;
